import { Fragment, RefObject, useRef, useState } from 'react';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import ProductTechnicalPictureGallery from '@features/productPreview/sections/ProductDescription/components/ProductDescriptionBlock/ProductTechnicalPictureGallery';
import { WindowType } from '@typescript/index';
import ProductDescriptionItem from '@features/productPreview/sections/ProductDescription/components/ProductDescriptionBlock/ProductDescriptionItem';
import ProductTechnicalPicture from '@features/productPreview/sections/ProductDescription/components/ProductDescriptionBlock/ProductTechnicalPicture';
import styles from '@features/productPreview/sections/ProductDescription/ProductDescription.styles';
import stylesDownload from '@features/productPreview/sections/DocumentsSection/DocumentColumn/DocumentsColumn.styles';
import { ContentIconType, ContentPictureType, ContentTechnicalType, IAttributesSection, IFinishSection, IPropertiesSection, ISectionDescriptionItem, ISectionParameter, ITechnicalSection, IDimensionsSection, IVideoSection, PlainHtmlType, ContentVideoItemType, IManufacturerGprsSection, IEnergyClassSection, IInstructionsCertificatesSection, IInformationPageUrlSection, IInformationSheetSection, IInformationLabelUrlSection } from '@features/productPreview/sections/ProductDescription/typescript';
import { uiState } from '@features/productPreview/mobX/state/context';
import sanitizeHtml from '@utils/sanitizeHtml';
import { getDimensions } from '@features/productPreview/sections/ProductDescription/helpers';
import ModalRootPortal from '@components/ModalRootPortal/ModalRootPortal';
import ProductProperties from '@features/productPreview/sections/ProductDescription/components/ProductProperties/ProductProperties';
import Icon from '@components/icons/Icon';
type PropsType = {
  items: ISectionDescriptionItem[];
  windowType: WindowType;
};

// Funkcja do transformacji HTML - zamienia <p data-id="..."> na <img src="..."> jeśli data-id znajduje się w files.thumb
const transformContentWithImages = (content: PlainHtmlType, files?: ContentVideoItemType[]): string => {
  const thumbUrls = files?.map(file => file.type === 'ENERGY_CLASS_FILE' && file.thumb) || [];
  if (!files || files.length === 0) {
    return sanitizeHtml(content).__html;
  }
  let htmlContent = content;
  const pTagRegex = /<p[^>]*data-id\s*=\s*['"]([^'"]+)['"][^>]*>(.*?)<\/p>/gi;
  htmlContent = htmlContent.replace(pTagRegex, (match, dataIdValue) => {
    if (thumbUrls.includes(dataIdValue)) {
      // Zamieniamy <p> na <img>
      return `<img src="${dataIdValue}" alt="" style="max-width: 70%; height: auto;" />`;
    }
    return match;
  });
  return htmlContent;
};
const isAttributeType = (item: ISectionDescriptionItem): item is IAttributesSection => (item as IAttributesSection).descriptionCode === 'PRODUCT_ATTRIBUTES';
const isFinishType = (item: ISectionDescriptionItem): item is IFinishSection => (item as IFinishSection).descriptionCode === 'PRODUCT_FINISH';
const isVideoType = (item: ISectionDescriptionItem): item is IVideoSection => (item as IVideoSection).descriptionCode === 'PRODUCT_VIDEO';
const isTechnicalType = (item: ISectionDescriptionItem): item is ITechnicalSection => (item as ITechnicalSection).descriptionCode === 'PRODUCT_TECHNICAL';
const isDimensionsType = (item: ISectionDescriptionItem): item is IDimensionsSection => (item as IDimensionsSection).descriptionCode === 'PRODUCT_DIMENSIONS';
const isPropertiesType = (item: ISectionDescriptionItem): item is ITechnicalSection => (item as IPropertiesSection).descriptionCode === 'PRODUCT_PROPERTIES';
const isManufacturerGprs = (item: ISectionDescriptionItem): item is IManufacturerGprsSection => (item as IManufacturerGprsSection).descriptionCode === 'PRODUCT_MANUFACTURER';
const isenergyClassType = (item: ISectionDescriptionItem): item is IEnergyClassSection => (item as IEnergyClassSection).descriptionCode === 'PRODUCT_ENERGY_CLASS';
const isInformationPageUrlType = (item: unknown): item is IInformationPageUrlSection => (item as IInformationPageUrlSection).descriptionCode === 'PRODUCT_INFORMATION_PAGE_URL';
const isInformationSheetType = (item: unknown): item is IInformationSheetSection => (item as IInformationSheetSection).descriptionCode === 'PRODUCT_INFORMATION_SHEET';
const isInformationLabelUrlType = (item: ISectionDescriptionItem): item is IInformationLabelUrlSection => (item as IInformationLabelUrlSection).descriptionCode === 'PRODUCT_INFORMATION_LABEL_URL';
const isInstructionsCertificatesType = (item: ISectionDescriptionItem): item is IInstructionsCertificatesSection => (item as IInstructionsCertificatesSection).descriptionCode === 'PRODUCT_INSTRUCTIONS_CERTIFICATES';
const DescriptionsSection = ({
  items,
  windowType
}: PropsType) => {
  const [selectedIndex, setSelectedIndex] = useState<number[] | null>([0]);
  const prevProductDescriptionItem = useRef<HTMLDivElement | null>(null);
  const [isOpenGallery, setIsOpenGallery] = useState(false);
  const {
    selectedTechSlider
  } = uiState;
  const {
    t
  } = useTranslation('layouts');
  const {
    t: tCommon
  } = useTranslation('common');
  const handleDescriptionItemClick = (index: number, currRef: RefObject<HTMLDivElement | null>, childrenRef: RefObject<HTMLDivElement | null>) => {
    if (selectedIndex?.includes(index)) {
      setSelectedIndex(selectedIndex.filter(item => item !== index));
    } else {
      setSelectedIndex([...(selectedIndex ?? []), index]);
      if (currRef.current) {
        prevProductDescriptionItem.current = childrenRef.current;
      }
    }
  };
  return <>
      {items.map((item: ISectionDescriptionItem, index) => {
      const {
        title
      } = item;
      let details: React.ReactNode | null = null;
      if (isenergyClassType(item) || isInformationPageUrlType(item) || isInformationSheetType(item) || isInformationLabelUrlType(item)) return null;
      if (item.content) {
        const htmlContent = transformContentWithImages(item.content as PlainHtmlType, item.files);
        details = <div dangerouslySetInnerHTML={{
          __html: htmlContent.replace(/<div>\s*<\/div>/g, '').replaceAll('<div>', '<p class="contentWrapper">').replaceAll('</div>', '</p>').replace(/(<br\s*\/?>\s*)+<\/p>/g, '</p>')
        }} sx={styles.productContainerContent} />;
      }
      if (isManufacturerGprs(item)) {
        if (item.content) {
          details = <div dangerouslySetInnerHTML={{
            __html: transformContentWithImages(item.content as PlainHtmlType, item.files)
          }} sx={styles.productContainerContent} />;
        } else {
          details = null;
        }
      }
      if (isManufacturerGprs(item) || isInstructionsCertificatesType(item)) {
        if (item.content) {
          details = <div dangerouslySetInnerHTML={{
            __html: transformContentWithImages(item.content as PlainHtmlType, item.files)
          }} sx={styles.productContainerContent} />;
        } else if (item.files?.find(file => file.type === 'PRODUCT_DOCUMENT')) {
          details = <></>;
        } else {
          details = null;
        }
      }
      let technical: React.ReactNode | [] = [];
      if (isTechnicalType(item) && (item.content.length > 0 || item.content !== '')) {
        const dimensions: Array<ContentTechnicalType> = getDimensions(item.content);
        technical = dimensions.length > 0 ? dimensions.map((contentTech: ContentTechnicalType) => <span key={contentTech.dimension} sx={styles.technicalText}>
                    <span sx={styles.technicalDimensionName}>{contentTech.dimension}</span>:{' '}
                    {contentTech.fromLabel === 'true' ? tCommon('product.productCostFrom') : null} {contentTech.value}{' '}
                    {contentTech.unit}
                  </span>) : [];
        const dimensionDesc = items.find(obj => obj.descriptionCode === 'PRODUCT_DIMENSIONS');
        if (dimensionDesc) {
          details = item.content && <div dangerouslySetInnerHTML={sanitizeHtml(dimensionDesc.content as PlainHtmlType)} sx={styles.productContainerContent} />;
        } else {
          details = null;
        }
      }
      if (isDimensionsType(item) && (item.content.length > 0 || item.content !== '')) {
        const dimensionDesc = items.find(obj => obj.descriptionCode === 'PRODUCT_TECHNICAL');
        if (!dimensionDesc) {
          details = item.content && <div dangerouslySetInnerHTML={sanitizeHtml(item.content)} sx={styles.productContainerContent} />;
        } else {
          details = null;
        }
      }
      const techPicture = item.technicalPictureItems.length > 0 && <ProductTechnicalPicture picture={item.technicalPictureItems[0]} windowType={windowType} setIsOpenGallery={setIsOpenGallery} />;
      let attributes: React.ReactNode | null = null;
      if (isAttributeType(item)) {
        attributes = item.content.map((attribute: ContentIconType) => <li key={attribute.id} sx={styles.attributeItem}>
              <ResponsiveImage image={attribute.icon} width={60} height={60} dataCy="PRODUCT_ATTRIBUTE_ICON" alt="" />
              <p sx={styles.attributeText}>{attribute.description}</p>
            </li>);
        details = null;
      }
      let finish: React.ReactNode | null = null;
      if (isFinishType(item)) {
        finish = item.content.map((picture: ContentPictureType) => <li key={picture.id} sx={styles.pictureItem}>
              <ResponsiveImage image={picture.image} width={216} height={144} alt="" />
              <p sx={styles.pictureText}>{picture.description}</p>
            </li>);
        details = null;
      }
      let properties: ISectionParameter[] | null = null;
      if (isPropertiesType(item) && item.parameters && item.parameters?.length > 0) {
        properties = item.parameters;
        details = null;
      }
      if (details || Array.from(technical).length || finish || attributes || properties) {
        return <Fragment key={item.code + item.descriptionCode}>
              {!isVideoType(item) && <div sx={styles.descriptionContainer} data-cy="DescriptionContainerItem">
                  <ProductDescriptionItem title={title || ''} windowType={windowType} isExpanded={selectedIndex?.includes(index) ?? false} childrenRef={selectedIndex?.includes(index) ?? false ? prevProductDescriptionItem : null} onClick={(ref, childrenRef) => handleDescriptionItemClick(index, ref, childrenRef)}>
                    {attributes && <ul sx={styles.list}>{attributes}</ul>}
                    {finish && <div sx={styles.list}>{finish}</div>}
                    {properties && <ProductProperties properties={properties} />}

                    <div sx={styles.technicalContainer}>
                      {Array.from(technical).length > 0 && <div sx={styles.technicalText}>
                          {technical}
                          {details && <div sx={styles.descriptionDetails}>{details}</div>}
                        </div>}
                      {Array.from(technical).length === 0 && details && details && <div sx={styles.descriptionDetails}>{details}</div>}
                      {item.files && item.files.length > 0 && <div sx={styles.descriptionDetails}>
                          {item.files.filter(file => file.type === 'PRODUCT_DOCUMENT').map(file => <a href={file.url} target="_blank" sx={stylesDownload.downloadLink} key={file.name} rel="noreferrer" title={`${file.name} ${t('footer.links.opensInNewWindow')}`}>
                                <Icon icon="Download" size="24px" /> <span>{file.name}</span>
                              </a>)}
                        </div>}
                      {techPicture}
                    </div>
                  </ProductDescriptionItem>
                  {item.technicalPictureItems.length > 0 && selectedTechSlider && isOpenGallery && <ModalRootPortal>
                      <ProductTechnicalPictureGallery slides={item.technicalPictureItems} setIsOpenGallery={setIsOpenGallery} />
                    </ModalRootPortal>}
                </div>}
            </Fragment>;
      }
      return null;
    })}
    </>;
};
export default observer(DescriptionsSection);